import {getMipLevelSize, HalContext, HalImage, HalPainterImageCompositorRef} from "@cm/hal"
import {Box2Like, Vector2Like} from "@cm/math"
import {assertNever} from "@cm/utils"
import {AddressMode} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/basic-definitions"
import {getHalAddressMode} from "@cm/webgl2"

export class MipmapHelperHal {
    constructor(private halContext: HalContext) {
        this._data = {
            generateMipMapCompositor: this.halContext.createHalPainterImageCompositor(
                `
            uniform ivec2 u_sourceOffset;
            uniform float u_scalingFactor;
            uniform int u_addressMode;
            
            vec4 computeColor(ivec2 targetPixel) {
                ivec2 sourcePixel = targetPixel * 2 + u_sourceOffset;
                vec4 c00 = texelFetch0(sourcePixel + ivec2(0, 0), u_addressMode);
                vec4 c10 = texelFetch0(sourcePixel + ivec2(1, 0), u_addressMode);
                vec4 c01 = texelFetch0(sourcePixel + ivec2(0, 1), u_addressMode);
                vec4 c11 = texelFetch0(sourcePixel + ivec2(1, 1), u_addressMode);
                return (c00 + c10 + c01 + c11) * 0.25 * u_scalingFactor;
            }
            `,
            ),
            generateBinomial4MipMapCompositorH: this.halContext.createHalPainterImageCompositor(
                `
            uniform ivec2 u_sourceOffset;
            uniform int u_addressMode;
            
            vec4 computeColor(ivec2 targetPixel) {
                ivec2 sourcePixel = ivec2(targetPixel.x * 2, targetPixel.y) + u_sourceOffset;
                vec4 cn1 = texelFetch0(sourcePixel + ivec2(-1, 0), u_addressMode);
                vec4 c0 = texelFetch0(sourcePixel + ivec2(0, 0), u_addressMode);
                vec4 c1 = texelFetch0(sourcePixel + ivec2(1, 0), u_addressMode);
                vec4 c2 = texelFetch0(sourcePixel + ivec2(2, 0), u_addressMode);
                return (cn1 + c2 + (c0  + c1) * 3.0) * (1.0 / 8.0);
            }
            `,
            ),
            generateBinomial4MipMapCompositorV: this.halContext.createHalPainterImageCompositor(
                `
            uniform ivec2 u_sourceOffset;
            uniform float u_scalingFactor;
            uniform int u_addressMode;
            
            vec4 computeColor(ivec2 targetPixel) {
                ivec2 sourcePixel = ivec2(targetPixel.x, targetPixel.y * 2) + u_sourceOffset;
                vec4 cn1 = texelFetch0(sourcePixel + ivec2(0, -1), u_addressMode);
                vec4 c0 = texelFetch0(sourcePixel + ivec2(0, 0), u_addressMode);
                vec4 c1 = texelFetch0(sourcePixel + ivec2(0, 1), u_addressMode);
                vec4 c2 = texelFetch0(sourcePixel + ivec2(0, 2), u_addressMode);
                return (cn1 + c2 + (c0  + c1) * 3.0) * (1.0 / 8.0) * u_scalingFactor;
            }
            `,
            ),
            generateBinomial6MipMapCompositorH: this.halContext.createHalPainterImageCompositor(
                `
            uniform ivec2 u_sourceOffset;
            uniform int u_addressMode;
            
            vec4 computeColor(ivec2 targetPixel) {
                ivec2 sourcePixel = ivec2(targetPixel.x * 2, targetPixel.y) + u_sourceOffset;
                vec4 cn2 = texelFetch0(sourcePixel + ivec2(-2, 0), u_addressMode);
                vec4 cn1 = texelFetch0(sourcePixel + ivec2(-1, 0), u_addressMode);
                vec4 c0 = texelFetch0(sourcePixel + ivec2(0, 0), u_addressMode);
                vec4 c1 = texelFetch0(sourcePixel + ivec2(1, 0), u_addressMode);
                vec4 c2 = texelFetch0(sourcePixel + ivec2(2, 0), u_addressMode);
                vec4 c3 = texelFetch0(sourcePixel + ivec2(3, 0), u_addressMode);
                return (cn2 + c3 + (cn1 + c2) * 5.0 + (c0  + c1) * 10.0) * (1.0 / 32.0);
            }
            `,
            ),
            generateBinomial6MipMapCompositorV: this.halContext.createHalPainterImageCompositor(
                `
            uniform ivec2 u_sourceOffset;
            uniform float u_scalingFactor;
            uniform int u_addressMode;
            
            vec4 computeColor(ivec2 targetPixel) {
                ivec2 sourcePixel = ivec2(targetPixel.x, targetPixel.y * 2) + u_sourceOffset;
                vec4 cn2 = texelFetch0(sourcePixel + ivec2(0, -2), u_addressMode);
                vec4 cn1 = texelFetch0(sourcePixel + ivec2(0, -1), u_addressMode);
                vec4 c0 = texelFetch0(sourcePixel + ivec2(0, 0), u_addressMode);
                vec4 c1 = texelFetch0(sourcePixel + ivec2(0, 1), u_addressMode);
                vec4 c2 = texelFetch0(sourcePixel + ivec2(0, 2), u_addressMode);
                vec4 c3 = texelFetch0(sourcePixel + ivec2(0, 3), u_addressMode);
                return (cn2 + c3 + (cn1 + c2) * 5.0 + (c0  + c1) * 10.0) * (1.0 / 32.0) * u_scalingFactor;
            }
            `,
            ),
            generateMipMapCompositorCentered: this.halContext.createHalPainterImageCompositor(
                `
            uniform ivec2 u_sourceOffset;
            uniform float u_scalingFactor;
            uniform int u_addressMode;
            
            vec4 computeColor(ivec2 targetPixel) {
                ivec2 sourcePixel = targetPixel * 2 + u_sourceOffset;
                vec4 cnn = texelFetch0(sourcePixel + ivec2(-1, -1), u_addressMode);
                vec4 czn = texelFetch0(sourcePixel + ivec2( 0, -1), u_addressMode);
                vec4 cpn = texelFetch0(sourcePixel + ivec2(+1, -1), u_addressMode);
                vec4 cnz = texelFetch0(sourcePixel + ivec2(-1,  0), u_addressMode);
                vec4 czz = texelFetch0(sourcePixel + ivec2( 0,  0), u_addressMode);
                vec4 cpz = texelFetch0(sourcePixel + ivec2(+1,  0), u_addressMode);
                vec4 cnp = texelFetch0(sourcePixel + ivec2(-1, +1), u_addressMode);
                vec4 czp = texelFetch0(sourcePixel + ivec2( 0, +1), u_addressMode);
                vec4 cpp = texelFetch0(sourcePixel + ivec2(+1, +1), u_addressMode);
                return ((cnn + cpn + cnp + cpp) * (1.0 / 16.0) + (czn + cnz + cpz + czp) * (1.0 / 8.0) + czz * (1.0 / 4.0)) * u_scalingFactor;
            }
            `,
            ),
        }
    }

    dispose() {
        if (!this._data) {
            throw new Error("MipmapHelperHal: Already disposed")
        }
        this._data.generateMipMapCompositor.release()
        this._data.generateBinomial4MipMapCompositorH.release()
        this._data.generateBinomial4MipMapCompositorV.release()
        this._data.generateBinomial6MipMapCompositorH.release()
        this._data.generateBinomial6MipMapCompositorV.release()
        this._data.generateMipMapCompositorCentered.release()
        this._data = null
    }

    // halo beyond 0/1 which is used for mipmap generation
    getHaloWidth(smoothingMode: SmoothingMode) {
        switch (smoothingMode) {
            case "none":
                return 0
            case "binomial4":
                return 1
            case "binomial6":
                return 2
            default:
                assertNever(smoothingMode)
        }
    }

    generateMipMap(args: {
        source: HalImage
        target: HalImage
        options?: {
            centered?: boolean
            sourceOffset?: Vector2Like
            sourceMipLevel?: number
            targetMipLevel?: number
            targetRegion?: Box2Like
            scalingFactor?: number
            smoothing?: SmoothingMode // default: "none"
            addressMode?: AddressMode // default: "clamp"
        }
    }) {
        if (!this._data) {
            throw new Error("MipmapHelperHal: Already disposed")
        }
        const targetMipLevel = args.options?.targetMipLevel ?? 0
        const targetRegion = args.options?.targetRegion ?? {
            x: 0,
            y: 0,
            ...getMipLevelSize(args.target.descriptor, targetMipLevel),
        }
        const sourceMipLevel = args.options?.sourceMipLevel ?? 0
        const sourceImage = args.source.createView({
            type: "sub-image",
            mipLevelOffset: sourceMipLevel,
        })
        const centered = args.options?.centered ?? false
        const smoothingMode: SmoothingMode = args.options?.smoothing ?? "none"
        const addressMode = getHalAddressMode(args.options?.addressMode ?? "clamp")
        if (smoothingMode === "binomial4" || smoothingMode === "binomial6") {
            if (centered) {
                throw new Error("Smoothed centered sampling is currently not supported")
            }
            const halo = this.getHaloWidth(smoothingMode)
            const compositorH = smoothingMode === "binomial4" ? this._data.generateBinomial4MipMapCompositorH : this._data.generateBinomial6MipMapCompositorH
            const compositorV = smoothingMode === "binomial4" ? this._data.generateBinomial4MipMapCompositorV : this._data.generateBinomial6MipMapCompositorV
            const tempImage = this.halContext.createPhysicalImage({
                width: targetRegion.width,
                height: targetRegion.height * 2 + halo * 2,
                channelLayout: sourceImage.descriptor.channelLayout,
                dataType: sourceImage.descriptor.dataType,
            })
            compositorH.ref.paint({
                target: tempImage.ref,
                sourceImages: sourceImage,
                parameters: {
                    u_sourceOffset: {
                        type: "int2",
                        value: {
                            x: targetRegion.x * 2 + (args.options?.sourceOffset?.x ?? 0),
                            y: targetRegion.y * 2 + (args.options?.sourceOffset?.y ?? 0),
                        },
                    },
                    u_addressMode: {type: "int", value: addressMode},
                },
            })
            compositorV.ref.paint({
                target: args.target,
                sourceImages: tempImage.ref,
                parameters: {
                    u_sourceOffset: {
                        type: "int2",
                        value: {
                            x: -targetRegion.x,
                            y: -targetRegion.y * 2,
                        },
                    },
                    u_scalingFactor: {type: "float", value: args.options?.scalingFactor ?? 1},
                    u_addressMode: {type: "int", value: addressMode},
                },
                options: {
                    targetMipLevel: targetMipLevel,
                    targetRegion: targetRegion,
                },
            })
            tempImage.release()
        } else {
            const compositor = centered ? this._data.generateMipMapCompositorCentered : this._data.generateMipMapCompositor
            compositor.ref.paint({
                target: args.target,
                sourceImages: sourceImage,
                parameters: {
                    u_sourceOffset: {type: "int2", value: args.options?.sourceOffset ?? {x: 0, y: 0}},
                    u_scalingFactor: {type: "float", value: args.options?.scalingFactor ?? 1},
                    u_addressMode: {type: "int", value: addressMode},
                },
                options: {
                    targetMipLevel: targetMipLevel,
                    targetRegion: targetRegion,
                },
            })
        }
    }

    private _data: Data | null
}

type Data = {
    generateMipMapCompositor: HalPainterImageCompositorRef
    generateBinomial4MipMapCompositorH: HalPainterImageCompositorRef
    generateBinomial4MipMapCompositorV: HalPainterImageCompositorRef
    generateBinomial6MipMapCompositorH: HalPainterImageCompositorRef
    generateBinomial6MipMapCompositorV: HalPainterImageCompositorRef
    generateMipMapCompositorCentered: HalPainterImageCompositorRef
}

export type SmoothingMode = "none" | "binomial4" | "binomial6"
